import { ComponentProps, FC } from "react";
import { Link } from "react-router-dom";
import * as _ from "lodash";

import {
  CtraLayout,
  Col,
  PageHeader,
  Row,
  Skeleton,
  Typography,
  Button,
  Icon,
  Popover,
  RightOutlined,
  Analysis,
  Charts as ChartUI
} from "@ctra/components";

import { Enterprise, useTranslation } from "@ctra/i18n";
import { GAContext, useGoogleAnalytics } from "@ctra/analytics";
import { ChartEntity, Enterprise as App } from "@ctra/api";

import { DataDictionaryProvider } from "@base";
import { Chart, ChartAPIContext, ChartFilterContext } from "@chart";
import { useFarm } from "@farms";
import { CompiledRoutes, Routes } from "@routes";

import { LayoutProvider, SectionContext, useSection } from "../../../../providers";
import { GAActions } from "../../../../analytics";
import { StackNavigation } from "../StackNavigation";
import { Card } from "../Card";
import { Recursion } from "../../v2/Recursion";
import { SectionToolbar } from "../../shared/SectionToolbar";
import { UnsupportedAlert } from "../../shared/UnsupportedAlert";
import styles from "./Stack.module.less";

const { AsyncLayout } = CtraLayout;
const { ChartVariant } = ChartUI;

interface StackProps {
  /**
   * Tells Stack how many sections/charts to display when collapsed
   */
  collapsedCount?: number;
}

const { Paragraph } = Typography;

export enum SectionVariant {
  overview = "overview",
  farm = "farm"
}

/**
 * Compare charts button on dashboard
 * @todo move it to a new component
 */
const CompareChartsButton: FC = () => {
  const { t } = useTranslation();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();

  const {
    layouts: {
      dashboard: { compareCharts: compare }
    }
  } = Enterprise;

  return (
    <Popover
      key="compare-charts"
      content={t<string>(compare.popover.content)}
      placement="bottomLeft"
      overlayClassName={styles.Popover}
    >
      <Button
        type="primary"
        icon={<Icon component={Analysis} />}
        onClick={() => {
          const search = new URLSearchParams();

          /* istanbul ignore next */
          trackEvent(GAActions.goToCompareCharts, { label });

          App.history.push({
            pathname: Routes.app.analytics.compareCharts.index,
            search: search.toString()
          });
        }}
      >
        {t<string>(compare.title)}
      </Button>
    </Popover>
  );
};

/**
 * A stack of cards to layout
 * @deprecated in favor of SectionDashboard
 * @constructor
 */
export const Stack: FC<StackProps> = ({ collapsedCount }) => {
  const { category, trackEvent } = useGoogleAnalytics();
  const { farm } = useFarm();
  const { t } = useTranslation();
  const { section } = useSection();

  const {
    layouts: {
      dashboard: { loading, viewAnalytics }
    },
    chart
  } = Enterprise;

  /**
   * Render a chart for the card
   * @param dataDescriptorID
   */
  const renderChart: ComponentProps<typeof Card>["renderChart"] = (dataDescriptorID) => (
    <ChartFilterContext.Provider>
      <ChartAPIContext.Provider dataDescriptorID={dataDescriptorID} farmID={farm?.id}>
        <ChartAPIContext.Consumer>
          {({ chart: contextChart }) => (
            <>
              <Paragraph className={styles.ChartTitle}>
                {t<string>(chart.title(contextChart?.title as ChartEntity["title"]), {
                  makeDefaultValue: true
                })}
              </Paragraph>
              <Chart variant={ChartVariant.Embedded} key={dataDescriptorID} config={{ legend: false }} />
            </>
          )}
        </ChartAPIContext.Consumer>
      </ChartAPIContext.Provider>
    </ChartFilterContext.Provider>
  );

  return (
    <LayoutProvider farmID={farm?.id}>
      {({ entity: layout, supportedSections, unsupportedSections }, { isLoading: isLayoutLoading }) => {
        /**
         * Convert this to entries so that we can slice based on the callapsedCount prop
         */
        const sectionsEntries = _.entries(supportedSections);

        /**
         * Show the alert if there are non supported sections, or if its the analytics page and
         * not the overview (using collapsedCount) or on both analytics and overview if the supported section
         * entries are completely empty.
         */
        const isUnsupportedAlertVisible =
          !(_.isEmpty(unsupportedSections) || collapsedCount) || _.isEmpty(sectionsEntries);

        return (
          <AsyncLayout className={styles.Wrapper} loading={isLayoutLoading} tip={t<string>(loading)}>
            <GAContext.Provider
              value={{
                category,
                label: section ? `${section.title}` : ""
              }}
            >
              <ChartFilterContext.Provider>
                <PageHeader
                  title={
                    <StackNavigation
                      variant={farm ? SectionVariant.farm : SectionVariant.overview}
                      sections={supportedSections}
                      active={section?.id}
                      onSelect={(id) => {
                        App.history.push(
                          CompiledRoutes.app.analytics.section.index({
                            sectionSlug: supportedSections[id as string].slug
                          })
                        );
                      }}
                    />
                  }
                  /**
                   * no need to show comparision button when a section is selected,
                   * all charts have the icon present
                   */
                  extra={section ? <SectionToolbar sectionID={section.id} /> : <CompareChartsButton />}
                />
                <DataDictionaryProvider>
                  {(_dataDescriptors, { isLoading: isDataDictionaryLoading }) => (
                    <Skeleton
                      className={styles.Skeleton}
                      loading={(isLayoutLoading && !layout) || isDataDictionaryLoading}
                    >
                      {section ? (
                        <SectionContext.Provider sectionID={section.id}>
                          <Recursion
                            farmID={farm?.id}
                            sectionID={section.id}
                            metadata={{ rootSectionID: section.id }}
                          />
                        </SectionContext.Provider>
                      ) : (
                        <>
                          <Row gutter={[16, { sm: 0, md: 16 }]} className={styles.Stack}>
                            {sectionsEntries.slice(0, collapsedCount).map(([sectionID, section]) => (
                              <Col
                                lg={collapsedCount ? 24 / collapsedCount : 8}
                                md={12}
                                span={24}
                                key={sectionID}
                              >
                                <Card
                                  variant={farm ? SectionVariant.farm : SectionVariant.overview}
                                  onClick={() => {
                                    /* istanbul ignore next */
                                    trackEvent(GAActions.goToDashboard, { label: `${section.title}` });
                                    App.history.push(
                                      CompiledRoutes.app.analytics.section.index({
                                        sectionSlug: section.slug
                                      })
                                    );
                                  }}
                                  of={section}
                                  renderChart={renderChart}
                                >
                                  {sectionID}
                                </Card>
                              </Col>
                            ))}
                            {isUnsupportedAlertVisible && (
                              <UnsupportedAlert unsupportedSections={unsupportedSections} />
                            )}
                          </Row>
                          {sectionsEntries.length > (collapsedCount as number) && (
                            <Row className={styles.ViewAnalytics} justify="center">
                              <Link
                                to={Routes.app.analytics.index}
                                onClick={() => {
                                  /* istanbul ignore next */
                                  trackEvent(GAActions.goToAllAnalytics);
                                }}
                              >
                                <Row align="middle">
                                  {t<string>(viewAnalytics)}&nbsp;
                                  <RightOutlined />
                                </Row>
                              </Link>
                            </Row>
                          )}
                        </>
                      )}
                    </Skeleton>
                  )}
                </DataDictionaryProvider>
              </ChartFilterContext.Provider>
            </GAContext.Provider>
          </AsyncLayout>
        );
      }}
    </LayoutProvider>
  );
};
